// Copyright 2018 Sorama B.V.

import { autoinject, bindable, computedFrom, observable, transient } from "aurelia-framework";
import { Point3D } from "datatypes/SoramaLocalApi";
import { MotionDeviceService } from "services/MotionDevice/MotionDeviceService";

@transient()
@autoinject
export class ScanController {
    private motionService: MotionDeviceService;

    @observable
    public stepSize = 0.16; // stepsize in m. This value get's added to x/y when pressing an arrow button

    private _xPosition: number;
    private _yPosition: number;
    private _zPosition: number;

    @bindable private animationName = "";

    public get xPosition(): number {
        return this.motionService.isActive || this._xPosition == undefined
            ? this.motionService.lastKnownPos.x
            : this._xPosition;
    }

    public set xPosition(val: number) {
        this._xPosition = val;
    }

    public get yPosition(): number {
        return this.motionService.isActive || this._yPosition == undefined
            ? this.motionService.lastKnownPos.y
            : this._yPosition;
    }

    public set yPosition(val: number) {
        this._yPosition = val;
    }

    public get zPosition(): number {
        return this.motionService.isActive || this._zPosition == undefined
            ? this.motionService.lastKnownPos.z
            : this._zPosition;
    }

    public set zPosition(val: number) {
        this._zPosition = val;
    }

    @computedFrom(
        "motionService.isActive",
        "motionService.connectedMotionDevice",
        "motionService.connectedMotionDevice.isHomed"
    )
    public get canMove(): boolean {
        const canMove =
            !this.motionService.isActive &&
            this.motionService.connectedMotionDevice &&
            this.motionService.connectedMotionDevice.isHomed;
        this.updateMovingAnimation();
        return canMove;
    }

    constructor(motionService: MotionDeviceService) {
        this.motionService = motionService;
    }

    private updateMovingAnimation(): void {
        if (!this.motionService.currentGoalPos) {
            return;
        }

        let animationName = "move";

        if (this.motionService.lastKnownPos) {
            if (this.motionService.currentGoalPos.y > this.yPosition) {
                animationName += "down";
            }
            if (this.motionService.currentGoalPos.y < this.yPosition) {
                animationName += "up";
            }
            if (this.motionService.currentGoalPos.x < this.xPosition) {
                animationName += "left";
            }
            if (this.motionService.currentGoalPos.x > this.xPosition) {
                animationName += "right";
            }
        }

        this.animationName = animationName;
    }

    public goToDeltaPosition(x: number, y: number, z = 0): void {
        this.goToPosition(
            Math.min(
                this.motionService.connectedMotionDevice.range.x,
                Math.max(0, this.motionService.lastKnownPos.x + x)
            ),
            Math.min(
                this.motionService.connectedMotionDevice.range.y,
                Math.max(0, this.motionService.lastKnownPos.y + y)
            ),
            Math.min(
                this.motionService.connectedMotionDevice.range.z,
                Math.max(0, this.motionService.lastKnownPos.z + z)
            )
        );
    }

    private goToPosition(x: number, y: number, z = 0) {
        this.motionService.moveTo(<Point3D>{ x: x, y: y, z: z }).then(() => this.updatePosition());
    }

    private home() {
        this.motionService.home();
    }

    private stop(): void {
        this.motionService.stop().then(() =>
            this.motionService
                .currentPosition()
                .then((x) => {
                    this.xPosition = x.x;
                    this.yPosition = x.y;
                    this.zPosition = x.z;
                })
                .catch((e) => console.error(e))
        );
    }

    private updatePosition(): void {
        this.motionService.currentPosition();
    }
}
